import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PaymentTerminalIpDialog } from "./PaymentTerminalIpDialog";
import { ConfigClient } from "../../clients/ConfigClient";
import { useLamp } from "../../Lamp";
import { usePrinter } from "../../Printer/printer";
import { ActionKind, ScannedCode } from "../../types/Action";
import { PaymentTerminalStatus } from "../../types/Config";
import { OperatorSubStateKind } from "../../types/Operator";
import { httpClient } from "../../utils/http";
import { Button } from "../Button";
import { useConfig } from "../ConfigProvider";
import useBarcodeScanner from "../hooks/useBarcodeScanner";
import { RootStateKind, useRootAndSubState } from "../state/StateProvider";

const sectionLabelClassName = "text-2xl font-semibold text-primary-900";
const germanyCountryCode = "DE";
export const Hardware = () => {
    const { dispatch } = useRootAndSubState(
        RootStateKind.Operator,
        OperatorSubStateKind.Hardware
    );
    const { setColor, setBlink } = useLamp();
    const { print } = usePrinter();
    const { t } = useTranslation();
    const { store } = useConfig();

    const scannerTextarea = useRef<HTMLTextAreaElement>(null);

    const [isIpDialogOpen, setIsIpDialogOpen] = useState(false);
    const [paymentTerminalStatus, setPaymentTerminalStatus] = useState<
        PaymentTerminalStatus | undefined
    >(undefined);
    const [currentIp, setCurrentIp] = useState<string | undefined>(undefined);

    const refreshStatus = () =>
        new ConfigClient(httpClient)
            .systemInformation()
            .then((info) => {
                setPaymentTerminalStatus(info.paymentDeviceStatus);
                setCurrentIp(info.paymentDeviceIp);
            })
            .catch(() => {
                setPaymentTerminalStatus(undefined);
                setCurrentIp(undefined);
            });

    useEffect(() => {
        refreshStatus();
    }, []);

    const onScan = ({ code }: ScannedCode) => {
        if (scannerTextarea.current) {
            scannerTextarea.current.value = code;
        }
    };

    useBarcodeScanner({ onScan });

    return (
        <div className="m-4 flex h-full flex-col gap-4 overflow-y-auto pb-16">
            <div className="text-3xl font-semibold text-primary-900">
                {t("operator.hardware.title")}
            </div>
            <div className="flex flex-col gap-4">
                <div className={sectionLabelClassName}>
                    {t("operator.hardware.paymentTerminal")}
                </div>
                {paymentTerminalStatus && (
                    <div data-testid="payment-device-status">
                        {t("operator.hardware.paymentDeviceConfig.status")}:{" "}
                        <span className="font-semibold">
                            {t(
                                `operator.hardware.paymentDeviceConfig.statuses.${paymentTerminalStatus}`
                            )}
                        </span>
                    </div>
                )}
                <div>
                    {t("operator.hardware.paymentDeviceConfig.currentIp")}:{" "}
                    <span className="font-semibold" data-testid="current-ip">
                        {currentIp || t("unknown")}
                    </span>
                </div>
                <div className="flex gap-4">
                    <Button
                        variant="secondary"
                        data-testid="payment-device-alter-ip"
                        onClick={() => setIsIpDialogOpen(true)}
                    >
                        {t("operator.hardware.paymentDeviceConfig.alterIp")}
                    </Button>
                    <Button
                        variant="secondary"
                        data-testid="payment-terminal-probe"
                        onClick={async () => {
                            await dispatch({
                                kind: ActionKind.ProbePaymentTerminal
                            });
                            await refreshStatus();
                        }}
                    >
                        {t("operator.hardware.paymentDeviceConfig.probe")}
                    </Button>
                    {store.country === germanyCountryCode && (
                        <Button
                            variant="secondary"
                            data-testid="payment-device-login"
                            onClick={async () => {
                                await dispatch({
                                    kind: ActionKind.PaymentDeviceLogin
                                });
                                await refreshStatus();
                            }}
                        >
                            {t("operator.hardware.login")}
                        </Button>
                    )}
                </div>
            </div>
            {isIpDialogOpen && (
                <PaymentTerminalIpDialog
                    onClose={() => setIsIpDialogOpen(false)}
                    onIpUpdated={refreshStatus}
                />
            )}
            <div className="flex flex-col gap-4">
                <div className={sectionLabelClassName}>
                    {t("operator.hardware.lamp")}
                </div>
                <div className="flex gap-4">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setColor("Red");
                        }}
                    >
                        {t("operator.hardware.red")}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setColor("Green");
                        }}
                    >
                        {t("operator.hardware.green")}
                    </Button>
                </div>
                <div>{t("operator.hardware.blink")}</div>
                <div className="flex gap-4">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setBlink("Red").then(() => setColor("Red"));
                        }}
                    >
                        {t("operator.hardware.red")}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setBlink("Green").then(() => setColor("Green"));
                        }}
                    >
                        {t("operator.hardware.green")}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                <div className={sectionLabelClassName}>
                    {t("operator.hardware.printer")}
                </div>
                <div className="flex gap-4">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            print(dummyReceipt);
                        }}
                    >
                        {t("operator.hardware.print")}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                <div className={sectionLabelClassName}>
                    {t("operator.hardware.scanner")}
                </div>
                <Button
                    variant="secondary"
                    className="w-min"
                    onClick={() => {
                        if (scannerTextarea.current) {
                            scannerTextarea.current.value = "";
                        }
                    }}
                >
                    {t("operator.hardware.clear")}
                </Button>
                <textarea
                    className="bg-gray-100"
                    ref={scannerTextarea}
                    rows={3}
                    autoFocus
                />
            </div>
        </div>
    );
};

const dummyReceipt = `<?xml version="1.0" encoding="UTF-8"?>
<main lng="de">
  <line>
    <text alignment="left">Valora Store </text>
  </line>
  <line>
    <text bold="true" alignment="left">01 January 2024 00:00</text>
  </line>
  <drawline />
  <br />
  <line>
    <text alignment="center">Valora Store </text>
  </line>
  <line>
    <text alignment="center">Valora Schweiz AG</text>
  </line>
  <line>
    <text alignment="center">CHE-103.468.185</text>
  </line>
  <br />
  <line>
    <text alignment="left">Kauf-ID: xxxxx-xxxxx</text>
    <text alignment="right">CHF</text>
    <tab alignment="right" />
    <tab alignment="right" />
    <tab alignment="right" />
  </line>
  <line>
    <text pad="4" alignment="left">1x</text>
    <tab alignment="left" />
    <text alignment="left">TRAGTASCHE 0.40 0.40 CHF</text>
    <text pad="6" alignment="right">0.40</text>
    <tab alignment="right" />
    <text alignment="right">AE</text>
  </line>
  <drawline />
  <br />
  <line>
    <text alignment="left">TOTAL [1]</text>
    <text alignment="center">CHF</text>
    <text alignment="right">0.40</text>
    <tab alignment="right" />
    <tab alignment="right" />
  </line>
  <line>
    <text alignment="left">VISA</text>
    <text alignment="center">CHF</text>
    <text alignment="right">0.40</text>
    <tab alignment="right" />
    <tab alignment="right" />
  </line>
  <drawline />
  <br />
  <line>
    <text alignment="left">Nettobetrag</text>
    <text alignment="right">CHF</text>
    <tab alignment="right" />
  </line>
  <line>
    <text alignment="left">AE=MWST</text>
    <text pad="6" alignment="center">8.10%</text>
    <tab alignment="center" />
    <text alignment="center">=</text>
    <text pad="6" alignment="right">0.03</text>
  </line>
  <drawline />
  <br />
  <line>
    <text bold="true" alignment="center">Buchung</text>
  </line>
  <line>
    <text bold="true" alignment="center">VISA CREDIT</text>
  </line>
  <line>
    <text alignment="left">XXXX XXXX XXXX XXXX</text>
  </line>
  <br />
  <line>
    <text bold="true" alignment="left">01.01.2024</text>
    <text bold="true" alignment="right">00:00:00</text>
  </line>
  <line>
    <text alignment="left">Trm-Id:</text>
    <text alignment="right">xxxxxxxx</text>
  </line>
  <line>
    <text alignment="left">Akt-Id:</text>
    <text alignment="right">x</text>
  </line>
  <line>
    <text alignment="left">AID:</text>
    <text alignment="right">Axxxxxxxxxxxxx</text>
  </line>
  <line>
    <text alignment="left">Trx. Seq-Cnt:</text>
    <text alignment="right">xxxxx</text>
  </line>
  <line>
    <text alignment="left">Trx. Ref-No:</text>
    <text alignment="right">xxxxxxxxxxx</text>
  </line>
  <line>
    <text alignment="left">Auth. Code:</text>
    <text alignment="right">xxxxxx</text>
  </line>
  <line>
    <text alignment="left">Acq-Id:</text>
    <text alignment="right">x</text>
  </line>
  <br />
  <line>
    <text alignment="left">Total-EFT CHF:</text>
    <text alignment="right">0.40</text>
  </line>
</main>
    `;
